import React, { useState, useEffect } from 'react';
import {
    FaUser,
    FaCalendarAlt,
    FaEnvelope,
    FaPhone,
    FaVenusMars,
    FaBirthdayCake,
    FaMapMarkerAlt,
    FaImage,
    FaClock,
    FaUserCheck,
    FaUserSlash
} from 'react-icons/fa';
import api from '../../services/api';

// Helper function to get correct image URL
const getProfileImage = (photoPath, gender) => {
    if (!photoPath) {
        return gender === 'Female'
            ? 'https://randomuser.me/api/portraits/women/85.jpg'
            : 'https://randomuser.me/api/portraits/men/85.jpg';
    }

    if (photoPath.startsWith('http') || photoPath.startsWith('https') || photoPath.startsWith('data:') || photoPath.startsWith('blob:')) {
        return photoPath;
    }
    const BASE_URL = api.defaults.baseURL;
    const baseUrl = BASE_URL.replace("/api", "");
    const cleanPath = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
    return `${baseUrl}${cleanPath}`;
};

const ProfileModeration = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/profiles/pending');
            setProfiles(res.data);
        } catch (error) {
            console.error('Failed to fetch pending profiles', error);
        } finally {
            setLoading(false);
        }
    };
    // Helper to calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return 'N/A';

        const birthDate = new Date(dob);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    };

    const handleAction = async (profileId, action) => {
        setActionLoading(profileId);

        try {
            if (action === 'approve') {
                await api.post('/admin/profiles/approve', { profileId });
            } else {
                await api.post('/admin/profiles/reject', { profileId });
            }
            fetchProfiles(); // Refresh list after action
        } catch (error) {
            console.error(`Failed to ${action} profile`, error);
            alert(`Failed to ${action} profile. Please try again.`);
        } finally {
            setActionLoading(null);
        }
    };

    // Loading State
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading pending profiles...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Profile Moderation</h1>
                    <p className="text-gray-600 mt-1">Review and approve user profile submissions</p>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={fetchProfiles}
                        className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                    >
                        Refresh List
                    </button>
                    <div className="px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl font-medium">
                        {profiles.length} Pending
                    </div>
                </div>
            </div>

            {/* Profiles Grid */}
            {profiles.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-16 text-center">
                    <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                        <FaUserCheck className="text-4xl text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">All Profiles Moderated</h3>
                    <p className="text-gray-600">There are no pending profiles waiting for review.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {profiles.map(profile => {
                        // Safely extract user info with fallback
                        const user = profile.userId || {};
                        const userName = user.name || 'Unknown User';
                        const userEmail = user.email || 'No email';
                        const userMobile = user.mobile || 'No mobile';

                        // Get main photo with fallback
                        const mainPhoto = profile.photos && profile.photos.length > 0
                            ? profile.photos[0]
                            : null;

                        const gender = profile.basicInfo?.gender || 'Male'; // fallback to Male for avatar

                        return (
                            <div key={profile._id} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                {/* Profile Image Header */}
                                <div className="relative h-56 overflow-hidden">
                                    {mainPhoto ? (
                                        <>
                                            <img
                                                src={getProfileImage(mainPhoto, gender)}
                                                alt={profile.basicInfo?.firstName || 'User'}
                                                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = gender === 'Female'
                                                        ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                        : 'https://randomuser.me/api/portraits/men/85.jpg';
                                                }}
                                            />
                                            <div className="absolute top-4 right-4">
                                                <span className="px-3 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                                                    <FaClock className="inline mr-1" /> Pending
                                                </span>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                                                <h3 className="text-white font-bold text-xl">
                                                    {profile.basicInfo?.firstName || 'N/A'} {profile.basicInfo?.lastName || ''}
                                                </h3>
                                                <div className="flex items-center text-white/90 text-sm mt-1">
                                                    <FaVenusMars className="mr-2" />
                                                    {profile.basicInfo?.gender || 'N/A'},{" "}
                                                    {calculateAge(profile.basicInfo?.dob)} years
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                            <div className="text-center">
                                                <FaImage className="text-6xl text-gray-300 mb-3" />
                                                <p className="text-gray-400 font-medium">No Photo</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Profile Details */}
                                <div className="p-6">
                                    {/* User Info */}
                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-center text-gray-600">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                                <FaUser className="text-blue-500" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-800">{userName}</p>
                                                <p className="text-sm text-gray-500 flex items-center">
                                                    <FaEnvelope className="mr-2" /> {userEmail}
                                                </p>
                                                {userMobile !== 'No mobile' && (
                                                    <p className="text-sm text-gray-500 flex items-center mt-1">
                                                        <FaPhone className="mr-2" /> {userMobile}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Profile Details Grid */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <FaBirthdayCake className="mr-2 text-gray-400" />
                                                <span>{new Date(profile.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600">
                                                <FaCalendarAlt className="mr-2 text-gray-400" />
                                                <span>Submitted</span>
                                            </div>
                                            {profile.basicInfo?.location && (
                                                <div className="col-span-2 flex items-center text-sm text-gray-600">
                                                    <FaMapMarkerAlt className="mr-2 text-gray-400" />
                                                    <span>{profile.basicInfo.location}</span>
                                                </div>
                                            )}
                                            {profile.basicInfo?.phoneNumber && (
                                                <div className="col-span-2 flex items-center text-sm text-gray-600">
                                                    <FaPhone className="mr-2 text-gray-400" />
                                                    <span>{profile.basicInfo.phoneNumber}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bio Preview */}
                                    {profile.basicInfo?.bio && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-2">About</h4>
                                            <p className="text-sm text-gray-600 line-clamp-3">{profile.basicInfo.bio}</p>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => handleAction(profile._id, 'approve')}
                                            disabled={actionLoading === profile._id}
                                            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-xl font-medium transition-all duration-200 shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {actionLoading === profile._id ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <>
                                                    <FaUserCheck /> Approve
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleAction(profile._id, 'reject')}
                                            disabled={actionLoading === profile._id}
                                            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-medium transition-all duration-200 shadow-lg shadow-red-500/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {actionLoading === profile._id ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <>
                                                    <FaUserSlash /> Reject
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Stats Footer */}
            {profiles.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center p-4 bg-blue-50 rounded-xl">
                            <div className="text-3xl font-bold text-blue-600 mb-2">{profiles.length}</div>
                            <p className="text-sm text-gray-600">Pending Profiles</p>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-xl">
                            <div className="text-3xl font-bold text-gray-600 mb-2">
                                {profiles.filter(p => p.photos?.length > 0).length}
                            </div>
                            <p className="text-sm text-gray-600">With Photos</p>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-xl">
                            <div className="text-3xl font-bold text-gray-600 mb-2">
                                {new Date().toLocaleDateString()}
                            </div>
                            <p className="text-sm text-gray-600">Today's Date</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileModeration;